/**
 * 반응형 환경에 따라 서로 다른 컴포넌트를 렌더링하는 헬퍼 컴포넌트입니다.
 *
 * 사용 예시
 * <Responsive desktop={<A />} mobile={<B />} />
 * <Responsive.DesktopOnly>데스크톱 전용</Responsive.DesktopOnly>
 * <Responsive.MobileOnly>모바일 전용</Responsive.MobileOnly>
 */
import { cn } from '@core/utils';

type ResponsiveDisplay =
  | 'block'
  | 'inline-block'
  | 'inline-flex'
  | 'flex'
  | 'grid';

interface ResponsiveProps {
  desktop: React.ReactNode;
  mobile: React.ReactNode;
}

interface ResponsiveSubComponentProps {
  children: React.ReactNode;
  className?: string;
  display?: ResponsiveDisplay;
}

const DESKTOP_DISPLAY_CLASSES: Record<ResponsiveDisplay, string> = {
  block: 'block',
  flex: 'flex',
  'inline-block': 'inline-block',
  'inline-flex': 'inline-flex',
  grid: 'grid',
};

const MOBILE_DISPLAY_CLASSES: Record<ResponsiveDisplay, string> = {
  block: 'mobile-tablet:block',
  flex: 'mobile-tablet:flex',
  'inline-block': 'mobile-tablet:inline-block',
  'inline-flex': 'mobile-tablet:inline-flex',
  grid: 'mobile-tablet:grid',
};

/**
 * 데스크톱 해상도에서만 children을 렌더링합니다.
 */
const DesktopOnly = ({
  children,
  className,
  display = 'block',
}: ResponsiveSubComponentProps) => (
  <div
    className={cn(
      DESKTOP_DISPLAY_CLASSES[display],
      'mobile-tablet:hidden',
      className,
    )}
  >
    {children}
  </div>
);

/**
 * 모바일/태블릿 해상도에서만 children을 렌더링합니다.
 */
const MobileOnly = ({
  children,
  className,
  display = 'block',
}: ResponsiveSubComponentProps) => (
  <div className={cn('hidden', MOBILE_DISPLAY_CLASSES[display], className)}>
    {children}
  </div>
);

const ResponsiveBase = ({ desktop, mobile }: ResponsiveProps) => {
  return (
    <>
      <DesktopOnly>{desktop}</DesktopOnly>
      <MobileOnly>{mobile}</MobileOnly>
    </>
  );
};

type ResponsiveComponent = typeof ResponsiveBase & {
  DesktopOnly: typeof DesktopOnly;
  MobileOnly: typeof MobileOnly;
};

export const Responsive = Object.assign(ResponsiveBase, {
  DesktopOnly,
  MobileOnly,
}) as ResponsiveComponent;
